
import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

interface LearnContent {
  id: string
  title: string
  duration: string
  completed: boolean
  content_type: 'video' | 'reading' | 'quiz' | 'infographic'
  video_url?: string
  infographic_url?: string
  content_text?: string
  order_index: number
}

interface ModuleData {
  id: string
  title: string
  description: string
  category: string
  coins: number
  image_url?: string
  completed: boolean
  content: LearnContent[]
}

export const useModuleData = (moduleId: string | undefined) => {
  const [moduleData, setModuleData] = useState<ModuleData | null>(null)
  const [loading, setLoading] = useState(true)
  const { toast } = useToast()

  const fetchModuleData = async () => {
    try {
      setLoading(true)
      
      // Fetch module details
      const { data: moduleInfo, error: moduleError } = await supabase
        .from('learn_modules')
        .select('*')
        .eq('id', moduleId)
        .single()

      if (moduleError) throw moduleError

      // Fetch module content
      const { data: contentData, error: contentError } = await supabase
        .from('learn_content')
        .select('*')
        .eq('module_id', moduleId)
        .order('order_index')

      if (contentError) throw contentError

      // Type assertion to ensure content_type matches our interface
      const typedContent: LearnContent[] = (contentData || []).map(item => ({
        ...item,
        content_type: item.content_type as 'video' | 'reading' | 'quiz' | 'infographic'
      }))

      const module: ModuleData = {
        ...moduleInfo,
        content: typedContent
      }

      setModuleData(module)
    } catch (error: any) {
      console.error('Error fetching module data:', error)
      toast({
        title: "Error",
        description: "Failed to load module data",
        variant: "destructive",
      })
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (moduleId) {
      fetchModuleData()
    }
  }, [moduleId])

  return { moduleData, loading, refetch: fetchModuleData }
}
